import { Button, DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import InstagramListPage from "@saleor/instagram/components/InstagramListPage";
import { useInstagramBulkPublish } from "@saleor/instagram/mutations";
import { useInstagramListQuery } from "@saleor/instagram/queries";
import {
  instagramListUrl,
  InstagramListUrlDialog,
  InstagramListUrlQueryParams,
  instagramUrl
} from "@saleor/instagram/urls";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface InstagramListProps {
  params: InstagramListUrlQueryParams;
}
const InstagramList: React.FC<InstagramListProps> = ({ params }) => {
  const tabs = getFilterTabs();

  const notify = useNotifier();

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(instagramListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };
  const intl = useIntl();

  const navigate = useNavigator();

  const paginate = usePaginator();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: instagramListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      instagramListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    InstagramListUrlDialog,
    InstagramListUrlQueryParams
  >(navigate, instagramListUrl, params);

  const [inseditBulkUpdate, instagramBulkPublishOpts] = useInstagramBulkPublish(
    {
      onCompleted: data => {
        if (data.inseditBulkUpdate.errors.length === 0) {
          notify({
            status: "success",
            text: intl.formatMessage(commonMessages.savedChanges)
          });
          refetch();
          closeModal();
        }
      }
    }
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.INSTAGRAMS_LIST
  );

  const paginationState = createPaginationState(settings?.rowNumber, params);

  // const queryVariables = React.useMemo(
  //     () => ({
  //     ...paginationState,
  //     filter: getFilterVariables(params),
  //     sort: getSortQueryVariables(params)
  //     }),
  //     [params]
  // );
  const queryVariables = {
    ...paginationState,
    filter: getFilterVariables(params),
    sort: getSortQueryVariables(params)
  };

  const { data, loading, refetch } = useInstagramListQuery({
    variables: queryVariables
  });
  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.instagrams.pageInfo),
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, instagramListUrl, params);
  const shop = useShop();
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");

  return (
    <>
      <InstagramListPage
        instagram={maybe(() => data.instagrams.edges.map(edge => edge.node))}
        toatlCount={maybe(() => data.instagrams.totalCount, 0)}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        currentTab={currentTab}
        onAdd={null}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={loading}
        settings={settings}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onSort={handleSort}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        sort={getSortParams(params)}
        onRowClick={id => () => navigate(instagramUrl(id))}
        toolbar={
          <>
            <Button
              color="primary"
              onClick={() =>
                openModal("unpublish", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="Unpublish"
                description="unpublish collections"
              />
            </Button>
            <Button
              color="primary"
              onClick={() =>
                openModal("publish", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="Publish"
                description="publish collections"
              />
            </Button>
          </>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
        currencySymbol={currencySymbol}
        filterOpts={getFilterOpts(params)}
        onFilterChange={changeFilters}
      />
      <ActionDialog
        open={
          params.action === "unpublish" && maybe(() => params.ids.length > 0)
        }
        onClose={closeModal}
        confirmButtonState={instagramBulkPublishOpts.status}
        onConfirm={() =>
          inseditBulkUpdate({
            variables: {
              ids: params.ids,
              published: false
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Unpublish collections",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to unpublish this collection?} other{Are you sure you want to unpublish {displayQuantity} collections?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "publish" && maybe(() => params.ids.length > 0)}
        onClose={closeModal}
        confirmButtonState={instagramBulkPublishOpts.status}
        onConfirm={() =>
          inseditBulkUpdate({
            variables: {
              ids: params.ids,
              published: true
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Publish collections",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to publish this collection?} other{Are you sure you want to publish {displayQuantity} collections?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};

export default InstagramList;
